/**
 * Small dialog to draw a signature (staff, in the admin app). Used where a registration's signature
 * is added or replaced: drawing replaces uploading an image.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SignaturePad } from '../public/components/SignaturePad';

interface SignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Throw to keep the dialog open and show the message */
  onSave: (png: Blob) => Promise<void>;
}

export function SignatureDialog({ open, onOpenChange, title, description, onSave }: SignatureDialogProps) {
  const [png, setPng] = useState<Blob | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (next: boolean) => {
    if (saving) return;
    if (!next) {
      setPng(null);
      setError(null);
    }
    onOpenChange(next);
  };

  const save = async () => {
    if (!png) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(png);
      setPng(null);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la firma');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <SignaturePad onChange={setPng} disabled={saving} />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={!png || saving}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar firma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
